<script>
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import { addObject, removeObject, findBy } from '@shell/utils/array';
import { sortBy } from '@shell/utils/sort';
import findIndex from 'lodash/findIndex';
import { ExtensionPoint, TabLocation } from '@shell/core/types';
import { getApplicableExtensionEnhancements } from '@shell/core/plugin-helpers';
import Tab from '@shell/components/Tabbed/Tab';
import { ref } from 'vue';
import { useIsInResourceDetailDrawer } from '@shell/components/Drawer/ResourceDetailDrawer/composables';
import { useIsInResourceDetailPage } from '@shell/composables/resourceDetail';
import { useIsInResourceCreatePage, useIsInResourceEditPage } from '@shell/composables/cruResource';

export default {
  name: 'Tabbed',

  components: { Tab },

  emits: ['changed', 'addTab', 'removeTab'],

  props: {
    defaultTab: {
      type:    String,
      default: null,
    },

    sideTabs: {
      type:    Boolean,
      default: false
    },

    hideSingleTab: {
      type:    Boolean,
      default: false
    },

    showTabsAddRemove: {
      type:    Boolean,
      default: false
    },

    // whether or not to scroll to the top of the new tab on tab change. This is particularly ugly with side tabs
    scrollOnChange: {
      type:    Boolean,
      default: false
    },

    useHash: {
      type:    Boolean,
      default: true,
    },

    noContent: {
      type:    Boolean,
      default: false,
    },

    // Remove padding and box-shadow
    flat: {
      type:    Boolean,
      default: false,
    },

    tabsOnly: {
      type:    Boolean,
      default: false,
    },

    resource: {
      type:    Object,
      default: () => {}
    },

    showExtensionTabs: {
      type:    Boolean,
      default: true,
    },
    /**
     * Inherited global identifier prefix for tests
     * Define a term based on the parent component to avoid conflicts on multiple components
     */
    componentTestid: {
      type:    String,
      default: 'tabbed'
    },

    removeBorders: {
      type:    Boolean,
      default: false,
    }
  },

  provide() {
    const tabs = this.tabs;

    return {
      sideTabs: this.sideTabs,

      addTab(tab) {
        const existing = findBy(tabs, 'name', tab.name);

        if ( existing ) {
          removeObject(tabs, existing);
        }

        addObject(tabs, tab);
      },

      removeTab(tab) {
        removeObject(tabs, tab);
      }
    };
  },

  data() {
    const location = this.getInitialTabLocation();
    let extensionTabs = this.showExtensionTabs ? getApplicableExtensionEnhancements(this, ExtensionPoint.TAB, location, this.$route, this, this.extensionParams) || [] : [];
    const legacyExtensionTabs = this.showExtensionTabs ? getApplicableExtensionEnhancements(this, ExtensionPoint.TAB, TabLocation.RESOURCE_DETAIL, this.$route, this, this.extensionParams) || [] : [];

    if (!extensionTabs.length) {
      // Support legacy tabs for RESOURCE_DETAIL location
      extensionTabs = legacyExtensionTabs;
    }

    const parsedExtTabs = extensionTabs.map((item) => {
      return {
        ...item,
        active: false
      };
    });

    return {
      tabs:          [...parsedExtTabs],
      extensionTabs: parsedExtTabs,
      activeTabName: null,
      tabRefs:       {}
    };
  },

  computed: {
    // keep the tabs list ordered for dynamic tabs
    sortedTabs() {
      return sortBy(this.tabs, ['weight:desc', 'labelDisplay', 'name']);
    },

    // hide tabs based on tab count IF flag is active
    hideTabs() {
      return this.hideSingleTab && this.sortedTabs.length === 1;
    },
  },

  setup() {
    const isInResourceDetailDrawer = ref(useIsInResourceDetailDrawer());
    const isInResourceDetailPage = ref(useIsInResourceDetailPage());
    const isInResourceEditPage = ref(useIsInResourceEditPage());
    const isInResourceCreatePage = ref(useIsInResourceCreatePage());

    return {
      isInResourceDetailDrawer, isInResourceDetailPage, isInResourceEditPage, isInResourceCreatePage
    };
  },

  watch: {
    sortedTabs(tabs) {
      const {
        defaultTab,
        useHash
      } = this;
      const activeTab = tabs.find((t) => t.active);

      const hash = useHash ? this.$route.hash : undefined;
      const windowHash = useHash ? hash.slice(1) : undefined;
      const windowHashTabMatch = tabs.find((t) => t.name === windowHash && !t.active);
      const firstTab = head(tabs) || null;

      if (isEmpty(activeTab)) {
        if (useHash && !isEmpty(windowHashTabMatch)) {
          this.select(windowHashTabMatch.name);
        } else if (!isEmpty(defaultTab) && !isEmpty(tabs.find((t) => t.name === defaultTab))) {
          this.select(defaultTab);
        } else if (firstTab?.name) {
          this.select(firstTab.name);
        }
      } else if (useHash && activeTab?.name === windowHash) {
        this.select(activeTab.name);
      }
    },
    '$route.hash'() {
      if ( this.useHash ) {
        this.hashChange();
      }
    }
  },

  methods: {
    getInitialTabLocation() {
      if (this.isInResourceEditPage) {
        return TabLocation.RESOURCE_EDIT_PAGE;
      } else if (this.isInResourceDetailDrawer) {
        return TabLocation.RESOURCE_SHOW_CONFIGURATION;
      } else if (this.isInResourceDetailPage) {
        return TabLocation.RESOURCE_DETAIL_PAGE;
      } else if (this.isInResourceCreatePage) {
        return TabLocation.RESOURCE_CREATE_PAGE;
      } else {
        return TabLocation.OTHER;
      }
    },
    hasErrorIcon(tab) {
      return tab.displayAlertIcon || (tab.error && !tab.active);
    },
    hashChange() {
      if (this.scrollOnChange) {
        const scrollable = document.getElementsByTagName('main')[0];

        if (scrollable) {
          scrollable.scrollTop = 0;
        }
      }

      this.select(this.$route.hash);
    },

    find(name) {
      return this.sortedTabs.find((x) => x.name === name );
    },

    select(name/* , event */) {
      const { sortedTabs } = this;

      const cleanName = name.replace('#', '');
      const selected = this.find(cleanName);
      const hashName = `#${ cleanName }`;

      if ( !selected || selected.disabled) {
        return;
      }
      /**
       * Exclude logic with URL anchor (hash) for projects without routing logic (vue-router)
       */
      if ( this.useHash ) {
        const currentRoute = this.$router.currentRoute._value;
        const routeHash = currentRoute.hash;

        if (this.useHash && routeHash !== hashName) {
          const kurrentRoute = { ...currentRoute };

          kurrentRoute.hash = hashName;

          this.$router.replace(kurrentRoute);
        }
      }

      for ( const tab of sortedTabs ) {
        tab.active = (tab.name === selected.name);
      }

      this.$emit('changed', { tab: selected, selectedName: selected.name });
      this.activeTabName = selected.name;
    },

    selectNext(direction) {
      const { sortedTabs } = this;
      const currentIdx = sortedTabs.findIndex((x) => x.active);
      const nextIdx = getCyclicalIdx(currentIdx, direction, sortedTabs.length);
      const nextName = sortedTabs[nextIdx].name;

      this.select(nextName);

      this.$nextTick(() => {
        this.$refs.tablist.removeAttribute('tabindex');
        if (this.tabRefs[nextName]) {
          this.tabRefs[nextName].focus();
        }
      });

      function getCyclicalIdx(currentIdx, direction, tabsLength) {
        const nxt = currentIdx + direction;

        if (nxt >= tabsLength) {
          return 0;
        } else if (nxt < 0) {
          return tabsLength - 1;
        } else {
          return nxt;
        }
      }
    },

    tabAddClicked() {
      const activeTabIndex = findIndex(this.tabs, (tab) => tab.active);

      this.$emit('addTab', activeTabIndex);
    },

    tabRemoveClicked() {
      const activeTabIndex = findIndex(this.tabs, (tab) => tab.active);

      this.$emit('removeTab', activeTabIndex);
    },
  },
};
</script>

<template>
  <div
    class="tabbed-container"
    :class="{
      'side-tabs': !!sideTabs,
      'tabs-only': tabsOnly,
      'remove-borders': removeBorders
    }"
    :data-testid="componentTestid"
  >
    <ul
      v-if="!hideTabs"
      ref="tablist"
      role="tablist"
      class="tabs"
      :class="{'clearfix':!sideTabs, 'vertical': sideTabs, 'horizontal': !sideTabs, 'remove-borders': removeBorders}"
      :data-testid="`${componentTestid}-block`"
      tabindex="0"
      @keydown.right.prevent="selectNext(1)"
      @keydown.left.prevent="selectNext(-1)"
      @keydown.down.prevent="selectNext(1)"
      @keydown.up.prevent="selectNext(-1)"
    >
      <!-- This is the tabs link... tabs appear here because they are injected from the "Tab" component -->
      <li
        v-for="tab in sortedTabs"
        :id="tab.name"
        :key="tab.name"
        :data-testid="tab.name"
        :class="{tab: true, active: tab.active, disabled: tab.disabled, error: (tab.error)}"
      >
        <a
          :id="`tab-${tab.name}`"
          :ref="(el) => { if (el) tabRefs[tab.name] = el; }"
          :data-testid="`btn-${tab.name}`"
          :aria-controls="tab.name"
          :aria-selected="tab.active"
          :aria-label="tab.labelDisplay || ''"
          role="tab"
          :tabindex="tab.active ? '0' : '-1'"
          @click.prevent="select(tab.name, $event)"
          @keyup.enter.space="select(tab.name, $event)"
        >
          <i
            v-if="tab.labelIcon"
            :class="`tab-label-icon icon ${tab.labelIcon}`"
          />
          <span>
            {{ tab.labelDisplay }}
          </span>
          <span
            v-if="tab.badge"
            class="tab-badge"
          >{{ tab.badge }}</span>
          <i
            v-if="hasErrorIcon(tab)"
            v-clean-tooltip="tab.errorIconTooltip || t('validation.tab')"
            class="conditions-alert-icon icon-error"
          />
        </a>
      </li>
      <li
        v-if="sideTabs && !sortedTabs.length"
        class="tab disabled"
      >
        <a
          href="#"
          @click.prevent
        >(None)</a>
      </li>
      <ul
        v-if="sideTabs && showTabsAddRemove"
        class="tab-list-footer"
      >
        <li>
          <button
            type="button"
            class="btn bg-transparent"
            data-testid="tab-list-add"
            :aria-label="t('tabs.addItem')"
            @click="tabAddClicked"
          >
            <i class="icon icon-plus" />
          </button>
          <button
            type="button"
            class="btn bg-transparent"
            :disabled="!sortedTabs.length"
            data-testid="tab-list-remove"
            :aria-label="t('tabs.removeItem')"
            @click="tabRemoveClicked"
          >
            <i class="icon icon-minus" />
          </button>
        </li>
      </ul>
      <slot name="tab-row-extras" />
    </ul>
    <div
      :class="{
        'tab-container': !!tabs.length || !!sideTabs,
        'no-content': noContent,
        'tab-container--flat': !!flat,
      }"
    >
      <!-- This is where "normal" tab content goes... -->
      <slot />
      <!-- Extension tabs content goes here... -->
      <Tab
        v-for="tab, i in extensionTabs"
        :key="`${tab.name}${i}`"
        :name="tab.name"
        :label="tab.label"
        :label-key="tab.labelKey"
        :label-icon="tab.labelIcon"
        :weight="tab.weight"
        :tooltip="tab.tooltip"
        :show-header="tab.showHeader"
        :display-alert-icon="tab.displayAlertIcon"
        :error="tab.error"
        :error-icon-tooltip="tab.errorIconTooltip"
        :badge="tab.badge"
      >
        <component
          :is="tab.component"
          :resource="resource"
        />
      </Tab>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tabbed-container {
  min-width: 0;
}

.tabs {
  list-style-type: none;
  margin: 0;
  padding: 0;

  &:focus-visible {
    outline: none;

    .tab.active {
      @include focus-outline;
      outline-offset: -2px;
    }
  }

  &.horizontal {
    border: 1px solid var(--dev-border, var(--border));
    border-radius: 8px 8px 0 0;
    background: var(--dev-layer, var(--body-bg));
    flex-wrap: nowrap;
    overflow-x: auto;
    border-bottom: 0;
    display: flex;
    flex-direction: row;

    &.remove-borders {
      border: none;

      + .tab-container {
        border: none;
        border-top: 1px solid var(--border);
        padding: 0;
        padding-top: 24px;
      }
    }

    + .tab-container {
      border: 1px solid var(--dev-border, var(--border));
      border-radius: 0 0 8px 8px;
    }

    .tab {
      border-bottom: 2px solid transparent;
    }

    .tab.active {
      border-bottom: solid 2px var(--active, var(--primary));
    }
  }

  &:focus .tab.active a span {
    text-decoration: underline;
  }

  .tab {
    position: relative;
    flex-shrink: 0;
    padding: 0;
    cursor: pointer;

    A {
      display: flex;
      align-items: center;
      min-height: 40px;
      box-sizing: border-box;
      padding: 10px 14px;
      white-space: nowrap;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      color: var(--dev-muted, var(--body-text));

      &:hover {
        background-color: var(--dev-accent-soft, var(--primary-light-bg));
        text-decoration: none;
      }

      &:focus-visible {
        @include focus-outline;
        outline-offset: -4px;
      }
    }

    .conditions-alert-icon {
      color: var(--error);
      padding-left: 4px;
      margin-left: auto;
    }

    &:last-child {
      padding-right: 0;
    }

    &.active {
      > A {
        color: var(--active, var(--primary));
        text-decoration: none;
      }
    }

    &.disabled {
      cursor: default;

      > A {
        color: var(--disabled-text);
        cursor: default;

        &:hover {
          background-color: transparent;
          text-decoration: none;
        }
      }
    }

    &.error {
      & A > .icon-error {
        color: var(--error);
      }
    }

    .tab-label-icon {
      margin-right: 8px;
    }

    .tab-badge {
      margin-left: 8px;
      background-color: var(--dev-accent-soft, var(--primary-light-bg));
      color: var(--dev-on-accent-soft, var(--link));
      border-radius: 4px;
      padding: 2px 6px;
      font-size: 11px;
      font-weight: 600;
      line-height: 1.3;
      font-variant-numeric: tabular-nums;
    }
  }
}

.tab-container {
  padding: 24px;
  min-width: 0;
  background: var(--dev-surface, var(--body-bg));

  &.no-content {
    padding: 0 0 3px 0;
  }

  // Example case: Tabbed component within a tabbed component
  &--flat {
    padding: 0;

    .side-tabs {
      box-shadow: unset;
    }
  }
}

.tabs-only {
  margin-bottom: 20px;

  .tab-container {
    display: none;
  }

  .tabs {
    border: 0;
    border-bottom: 2px solid var(--border);
  }
}

.side-tabs {
  display: flex;
  box-shadow: none;
  border: 1px solid var(--dev-border, var(--border));
  border-radius: 8px;
  background-color: var(--dev-layer, var(--tabbed-sidebar-bg));

  &.remove-borders {
    border: 0;
  }

  .tab-container {
    padding: 24px;
  }

  & .tabs {
    width: $sideways-tabs-width;
    min-width: $sideways-tabs-width;
    display: flex;
    flex: 0 0 $sideways-tabs-width;
    flex-direction: column;

    & .tab {
      width: 100%;
      border-left: solid 3px transparent;

      &.toggle A {
        color: var(--active, var(--primary));
      }

      A {
        color: var(--dev-muted, var(--body-text));
      }

      &.active {
        background-color: var(--dev-surface, var(--body-bg));
        border-left: solid 3px var(--active, var(--primary));

        & A {
          color: var(--input-label);
        }
      }

      &.disabled {
        background-color: var(--disabled-bg);

        & A {
          color: var(--disabled-text);
          text-decoration: none;
        }
      }
    }
    .tab-list-footer {
      list-style: none;
      padding: 0;
      margin-top: auto;
      z-index: z-index('default');

      li {
        display: flex;
        flex: 1;

        .btn {
          flex: 1 1;
          display: flex;
          justify-content: center;

          &:focus-visible {
            @include focus-outline;
          }
        }

        button:first-of-type {
          border-top: solid 1px var(--border);
          border-right: solid 1px var(--border);
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
        button:last-of-type {
          border-top: solid 1px var(--border);
          border-top-right-radius: 0;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    }
  }

  .tab-container {
    width: calc(100% - #{$sideways-tabs-width});
    flex: 1 1 auto;
    background-color: var(--dev-surface, var(--body-bg));
    border-radius: 0 8px 8px 0;
  }
}

@media (max-width: 760px) {
  .tab-container, .side-tabs .tab-container {
    padding: 16px;
  }

  .side-tabs {
    flex-direction: column;

    > .tabs {
      width: 100%;
      min-width: 0;
      flex: 0 0 auto;
      flex-direction: row;
      overflow-x: auto;
      border-bottom: 1px solid var(--dev-border, var(--border));

      .tab {
        width: auto;
        border-left: 0;
        border-bottom: 3px solid transparent;

        &.active {
          border-left: 0;
          border-bottom-color: var(--active, var(--primary));
        }
      }
    }

    > .tab-container {
      width: 100%;
      border-radius: 0 0 8px 8px;
    }
  }
}
</style>
